import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import _ from 'lodash';
import dayjs from 'dayjs';
import Yup from '../../../utils/yup';
import utc from 'dayjs/plugin/utc';

import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import { DeleteDialogUI, FormButtonPanelUI, FormHeaderUI, FormBase } from '../../Interface';
import CheckListTemplateItem from './CheckListTemplateItem.jsx';
import CheckListTemplateReports from './CheckListTemplateReports.jsx';
import { CheckListTemplateFields } from './CheckListTemplateFields.jsx.jsx';
import checkListService from '../../../service/checkList.service.js';
import masterDataService from '../../../service/masterData.service.js';
import { generateAutocompleteItems } from '../../../utils/helper.js';

export const CheckListTemplatePage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    projectType: [],
  });

  const defaultCheckListItem = {
    id: null,
    isNew: true,
    name: '',
    createdBy: user.userId,
    visOrder: null,
    attachRequired: false,
    descriptionRequired: false,
    descriptionName: null,
  };

  const def = {
    name: '',
    projectTypeId: null,
    checkListTemplateItem: [],
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('checkListTemplate');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    masterDataService
      .projectTypeToForm()
      .then((data) => {
        const aData = {
          projectType: data,
        };

        const acOptions = {
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    projectTypeId: Yup.string().nullable(),
    checkListTemplateItem: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(),
        attachRequired: Yup.boolean(),
        descriptionRequired: Yup.boolean(),
        descriptionName: Yup.string().nullable(),
      })
    ),
  });

  const valuesToForm = () => {
    const { name, projectTypeId, checkListTemplateItem } = selectedValues;

    setInitValues({
      name,
      projectTypeId,
      checkListTemplateItem: checkListTemplateItem.map((ct) => ({
        id: ct.id,
        name: ct.name,
        createdBy: user.userId,
        visOrder: ct.visOrder,
        attachRequired: ct.attachRequired,
        descriptionRequired: ct.descriptionRequired,
        descriptionName: ct.descriptionName,
      })),
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      updatedBy: user.userId,
    };

    checkListService.updateCheckListTemplate(updData, selectedValues.id).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
    };

    checkListService
      .createCheckListTemplate(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  dayjs.extend(utc);

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    checkListService.deleteCheckListTemplate(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="CheckListTemplate"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(def);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <CheckListTemplateFields viewField={viewField} autoCompleteOptions={autoCompleteOptions} />
                    <CheckListTemplateItem user={user} disabled={viewField} defaultField={defaultCheckListItem} />
                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <CheckListTemplateReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
